import { useState } from 'react';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { useNavigate } from 'react-router';

import { Card } from '@repo/ui/Card';
import { Button } from '@repo/ui/Button';
import { DeleteDialog } from '@repo/ui/Dialog';
import { Form } from '@repo/ui/Form';
import { useZodForm } from '@repo/hooks/useZodForm';

import { DeleteBlockedNotice } from '@/components/DeleteBlockedNotice';

const DELETE_SWITCH = gql`
  mutation DeleteSwitch($id: ID!) {
    deleteSwitch(id: $id) {
      switch {
        id
      }
    }
  }
`;

interface DeleteSwitchProps {
  switchId: string;
  name: string;
  accountId: string;
  /** Numbers are still attached, so deleting is blocked. */
  hasDids: boolean;
  /** Still checking for attached numbers; delete stays disabled meanwhile. */
  checkingDids: boolean;
  /** The switch's Numbers tab, linked from the blocked notice. */
  numbersHref: string;
  /** The account's default (oldest) switch, which can never be deleted. */
  isDefault?: boolean;
}

/**
 * Delete card for a switch. The default switch shows the same card with the
 * button disabled and the reason underneath the title, so every switch's page
 * has the same layout.
 */
const DeleteSwitch = ({
  switchId,
  name,
  accountId,
  hasDids,
  checkingDids,
  numbersHref,
  isDefault = false,
}: DeleteSwitchProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const client = useApolloClient();

  const form = useZodForm({
    onSubmit: async () => {
      await client.mutate({ mutation: DELETE_SWITCH, variables: { id: switchId } });
      setIsOpen(false);
      navigate(`/a/${accountId}/switches`);
    },
  });

  return (
    <>
      <Card
        title="Delete Switch"
        description={
          isDefault
            ? "This is the account's default switch, so it can't be deleted. New numbers on this account are assigned to it automatically, but can be later assigned to other available switches."
            : undefined
        }
      >
        <div className="flex flex-col gap-4 items-start">
          {!isDefault && hasDids && <DeleteBlockedNotice noun="switch" numbersHref={numbersHref} />}
          <Button
            variant="danger"
            disabled={isDefault || hasDids || checkingDids}
            onClick={() => setIsOpen(true)}
          >
            Delete Switch
          </Button>
        </div>
      </Card>

      <DeleteDialog
        title="Are you sure you want to delete this switch?"
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        centered
        form={form}
        submitText="Yes, delete switch"
        cancelText="No, abort"
      >
        <Form.Error>
          Unable to delete switch. Please try again later or contact support if error persists.
        </Form.Error>
        <div className="flex flex-col col-span-2 gap-2 py-3 mb-7 w-full border border-r-0 border-l-0 border-grey-400/50">
          <span className="text-2xl font-bold">{name}</span>
          <span>ID: {switchId}</span>
        </div>
      </DeleteDialog>
    </>
  );
};

export default DeleteSwitch;
